import React, { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';

import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import { Button } from 'src/components/Button';
import { PermissionGuard } from 'src/components/PermissionGuard';
import { PERMISSIONS } from 'src/utils/permissions';
import NoResult from 'src/components/NoResult/NoResult';
import { ConfirmModal } from 'src/components/ConfirmModal';
import { useDeferredLoading } from 'src/hooks/useDeferredLoading';

import {
  createRole,
  deleteRole,
  fetchPermissions,
  fetchRoles,
  toPermissionCatalogItems,
  updateRole,
} from 'src/api/adminManagement';
import { AdminRoleRecord, PERMISSION_CATALOG, PermissionCatalogItem } from 'src/utils/rbacCatalog';

import Pagination from 'src/components/Pagination';

import RoleFormModal, { RoleSavePayload } from './components/RoleFormModal';
import { MANAGEMENT_PAGE_SIZE } from './constants';
import { DataTable, DataTableBody, DataTableWrap, Hint, TableActions, TablePaginationWrap, ToolbarRow } from './styled';
import { ManagementTableSkeleton } from './ManagementTableSkeleton';

const getRoleId = (role: AdminRoleRecord) => role._id ?? role.key;

const Roles: React.FC<{ onTotalChange?: Dispatch<SetStateAction<number | null>> }> = ({
  onTotalChange,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<AdminRoleRecord[]>([]);
  const [rolesFromApi, setRolesFromApi] = useState(false);
  const [permissionCatalog, setPermissionCatalog] = useState<PermissionCatalogItem[]>(PERMISSION_CATALOG);
  const [formRole, setFormRole] = useState<AdminRoleRecord | null | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<AdminRoleRecord | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ roles: list, fromApi, totalPages: pages, totalCount }, permRes] = await Promise.all([
        fetchRoles(currentPage, MANAGEMENT_PAGE_SIZE),
        fetchPermissions(),
      ]);
      setRoles(list);
      setRolesFromApi(fromApi);
      setTotalPages(Math.max(1, pages));
      onTotalChange?.(totalCount);
      setPermissionCatalog(
        permRes.permissions.length ? toPermissionCatalogItems(permRes.permissions) : PERMISSION_CATALOG,
      );
    } catch (e: any) {
      dispatch(
        setAppAlert({
          message: e?.response?.data?.error ?? 'Failed to load roles',
          isSuccess: false,
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [currentPage, dispatch, onTotalChange]);

  useEffect(() => {
    load();
  }, [load]);

  const notify = (message: string, isSuccess: boolean) => dispatch(setAppAlert({ message, isSuccess }));

  const handleSave = async (payload: RoleSavePayload) => {
    try {
      if (formRole) {
        await updateRole(getRoleId(formRole), payload);
        notify('Role updated', true);
      } else {
        await createRole(payload);
        notify('Role created', true);
      }
      await load();
    } catch (e: any) {
      notify(e?.response?.data?.error ?? `Failed to ${formRole ? 'update' : 'create'} role`, false);
      throw e;
    }
  };

  const requestDelete = (role: AdminRoleRecord) => {
    if (!rolesFromApi) {
      notify('Roles cannot be deleted while using the default role list.', false);
      return;
    }
    setDeleteTarget(role);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteRole(getRoleId(deleteTarget));
      notify('Role deleted', true);
      setDeleteTarget(null);
      await load();
    } catch (e: any) {
      notify(e?.response?.data?.error ?? e?.message ?? 'Failed to delete role', false);
    } finally {
      setDeleting(false);
    }
  };

  const showLoading = useDeferredLoading(loading, roles.length > 0);
  const columnCount = rolesFromApi ? 5 : 4;

  return (
    <>
      <Hint>
        View, create and edit roles. Assign permission keys to each role.{' '}
        {!rolesFromApi && 'Showing default role list until roles are loaded from the server.'}
      </Hint>
      <PermissionGuard permission={PERMISSIONS.ALL}>
        <ToolbarRow>
          <Button text="Create role" className="black" onClick={() => setFormRole(null)} />
        </ToolbarRow>
      </PermissionGuard>
      {roles.length > 0 || loading ? (
        <DataTableWrap>
          <DataTable>
            <thead>
              <tr>
                <th>Key</th>
                <th>Name</th>
                <th>Status</th>
                <th>Permissions</th>
                {rolesFromApi && <th>Actions</th>}
              </tr>
            </thead>
            <DataTableBody $dimmed={showLoading && roles.length > 0}>
              <ManagementTableSkeleton loading={loading} hasContent={roles.length > 0} columns={columnCount} />
              {roles.map((role) => (
                <tr key={getRoleId(role)}>
                  <td data-label="Key">{role.key}</td>
                  <td data-label="Name">{role.name}</td>
                  <td data-label="Status">{role.status}</td>
                  <td data-label="Permissions">{role.permissions.length ? role.permissions.join(', ') : '—'}</td>
                  {rolesFromApi && (
                    <td data-label="Actions">
                      <TableActions>
                        <PermissionGuard permission={PERMISSIONS.ALL}>
                          <Button
                            text="Edit"
                            className="outline small"
                            onClick={() => setFormRole(role)}
                          />
                          <Button
                            text="Delete"
                            className="outline small"
                            onClick={() => requestDelete(role)}
                          />
                        </PermissionGuard>
                      </TableActions>
                    </td>
                  )}
                </tr>
              ))}
            </DataTableBody>
          </DataTable>
          {totalPages > 1 && (
            <TablePaginationWrap>
              <Pagination
                nPages={totalPages}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                urlKey="rolesPage"
              />
            </TablePaginationWrap>
          )}
        </DataTableWrap>
      ) : (
        <NoResult />
      )}
      {formRole !== undefined && (
        <RoleFormModal
          role={formRole}
          permissionCatalog={permissionCatalog}
          onClose={() => setFormRole(undefined)}
          onSave={handleSave}
        />
      )}
      {deleteTarget && (
        <ConfirmModal
          title="Delete role"
          message={
            <>
              Are you sure you want to delete <strong>{deleteTarget.name}</strong> ({deleteTarget.key})? This action
              cannot be undone.
            </>
          }
          confirmText="Delete"
          confirmClassName="negative"
          onClose={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
          isLoading={deleting}
        />
      )}
    </>
  );
};

export default Roles;
